<script lang="ts">
// Copyright 2026 AllSpice. All rights reserved.

import {defineComponent, type PropType} from 'vue';
import {toggleHighlightElement} from '../../components/document_highlight.ts';
import {onNextFrame, vueInstance} from '../../domutils.ts';
import {SvgIcon} from '../../svg.ts';
import {useMultiPageSearchStore} from '../stores/multiPageSearch.ts';
import {buildMultiPageSearchResults, filterMultiPageSearchResults, type ComponentInstance, type NetInstance, type MultiPageSearchResult} from '../stores/multiPage/search.ts';
import {findPageContainer, openAndScrollToPage, waitForPageSvg} from './diff-list/page-navigation.ts';
import {isNetSearchDisabledSchematic} from '../../utils/schematic-format.ts';
import type {Store} from '../store.ts';
import type {ProjectPageDiff, MultiPageDiff, ProjectGroupNode, TreeNode} from '../types/file_tree.ts';
import {FILE_DIFF_STATUS} from '../types/file_tree.ts';
import {groupResultsByCategory} from '../../utils/search-grouping.ts';

type HighlightElementType = 'component' | 'net';

export type MultiPageSearchData = {
  expanded: boolean,
  currentHighlightSvg: SVGSVGElement | null,
  currentHighlightSelector: string | null,
  currentHighlightElementType: HighlightElementType,
  navigationAbortController: AbortController | null,
  escapeHandler: ((event: KeyboardEvent) => void) | null,
  mousedownOutsideHandler: ((event: MouseEvent) => void) | null,
};

/**
 * Multi-page schematic Component Search.
 *
 * Builds a cross-page component index by walking every sibling page of
 * the parent multi-page schematic file via the project-view store.
 * Supports cycling through multiple instances of the same reference
 * across pages with ← N of M → controls.
 */
export default defineComponent({
  name: 'MultiPageComponentSearch',
  components: {SvgIcon},
  inject: {
    untypedStore: {from: 'store'},
  },
  props: {
    file: {
      type: Object as PropType<ProjectPageDiff | MultiPageDiff | ProjectGroupNode>,
      required: true,
    },
    // The diff hash of the document this search is mounted on. Only meaningful
    // for a project group, where each document is its own diff box and the
    // shared search must know which document this instance renders.
    documentNameHash: {
      type: String,
      default: '',
    },
  },
  setup() {
    return {
      multiPageSearchStore: useMultiPageSearchStore(),
    };
  },

  data(): MultiPageSearchData {
    return {
      expanded: false,
      currentHighlightSvg: null,
      currentHighlightSelector: null,
      currentHighlightElementType: 'component' as HighlightElementType,
      navigationAbortController: null,
      escapeHandler: null,
      mousedownOutsideHandler: null,
    };
  },

  computed: {
    currentPageId() {
      // A project group has no single page: identity is the document this search
      // is mounted on, so ownership routes to the document that owns an instance.
      if (this.file.ty === 'project-group') {
        return this.documentNameHash || this.file.headPath;
      }
      const pageId = 'pageId' in this.file ? this.file.pageId : null;
      const nameHash = 'nameHash' in this.file ? this.file.nameHash : undefined;
      return pageId ?? nameHash ?? this.file.headPath;
    },
    componentIndex() {
      return this.multiPageSearchStore.getComponentIndex(this.file.headPath);
    },
    searchState() {
      return this.multiPageSearchStore.getSearchState(this.file.headPath);
    },
    isLoading() {
      return this.componentIndex?.loading ?? false;
    },
    errorMessage() {
      return this.componentIndex?.error ?? null;
    },
    indexByReference() {
      return this.componentIndex?.byReference ?? new Map<string, ComponentInstance[]>();
    },
    indexByNet() {
      return this.componentIndex?.byNet ?? new Map<string, NetInstance[]>();
    },
    disableSchematicNetSearch() {
      return isNetSearchDisabledSchematic(this.file.headPath);
    },
    searchTooltip() {
      return this.disableSchematicNetSearch ? 'Search for components' : 'Search for components and nets';
    },
    searchPlaceholder() {
      return this.disableSchematicNetSearch ? 'Find components...' : 'Find components or nets...';
    },
    showUnchangedSheets() {
      return this.store.diffFilterConfig.showUnchangedSheets.value;
    },
    isPageVisible() {
      if (this.showUnchangedSheets) return () => true;
      const hashMap = this.store.hashToPathMap.value;
      return (pageNameHash: string) => {
        const node = hashMap.get(pageNameHash);
        return !node || !('status' in node) || node.status !== FILE_DIFF_STATUS.UNCHANGED;
      };
    },
    filteredByReference() {
      if (this.showUnchangedSheets) return this.indexByReference;
      const filtered = new Map<string, ComponentInstance[]>();
      for (const [ref, instances] of this.indexByReference) {
        const visible = instances.filter((i) => this.isPageVisible(i.pageNameHash));
        if (visible.length) filtered.set(ref, visible);
      }
      return filtered;
    },
    filteredByNet() {
      const filtered = new Map<string, NetInstance[]>();
      for (const [net, instances] of this.indexByNet) {
        const visible = instances.filter((i) => this.isPageVisible(i.pageNameHash));
        if (visible.length) filtered.set(net, visible);
      }
      return filtered;
    },
    allResults() {
      return buildMultiPageSearchResults(this.filteredByReference, this.filteredByNet);
    },
    selectedReference() {
      return this.searchState.selectedReference;
    },
    selectedCategory() {
      return this.searchState.selectedCategory;
    },
    selectedIndex() {
      return this.searchState.selectedIndex;
    },
    selectedInstances(): (ComponentInstance | NetInstance)[] {
      if (!this.selectedReference || !this.selectedCategory) {
        return [];
      }

      if (this.selectedCategory === 'Net') {
        return this.filteredByNet.get(this.selectedReference) ?? [];
      }
      return this.filteredByReference.get(this.selectedReference) ?? [];
    },
    totalInstances() {
      return this.selectedInstances.length;
    },
    selectedPageName() {
      return this.selectedInstances[this.selectedIndex]?.pageName ?? '';
    },
    isSearchOwner() {
      return this.searchState.openPageId === this.currentPageId;
    },
    searchQuery: {
      get() {
        return this.searchState.searchQuery ?? '';
      },
      set(val: string) {
        const currentQuery = this.searchState.searchQuery ?? '';
        this.multiPageSearchStore.setSearchQuery(this.file.headPath, val);

        if (val !== currentQuery && this.selectedReference) {
          this.clearPinnedSelection();
        }
      },
    },
    filteredResults() {
      return filterMultiPageSearchResults(this.allResults, this.searchQuery);
    },
    visibleResults() {
      if (!this.selectedReference || !this.selectedCategory) {
        return this.filteredResults;
      }

      const selectedResult = this.filteredResults.find(
        (result) => result.key === this.selectedReference && result.category === this.selectedCategory,
      );
      return selectedResult ? [selectedResult] : this.filteredResults;
    },
    visibleResultsByCategory() {
      return groupResultsByCategory(this.visibleResults);
    },
    projectDocumentPaths(): Set<string> {
      const paths = new Set<string>();
      if (this.file.ty !== 'project-group') {
        return paths;
      }
      const walk = (nodes: TreeNode[]): void => {
        for (const node of nodes) {
          if ('children' in node && node.children) {
            walk(node.children as TreeNode[]);
          } else {
            paths.add(node.headPath);
          }
        }
      };
      walk(this.file.children);
      return paths;
    },
    store() {
      return this.untypedStore as unknown as Store;
    },
  },

  watch: {
    isSearchOwner(isSearchOwner) {
      if (isSearchOwner) {
        if (!this.expanded) {
          this.resumeFromPersistedStateSafely();
        }
        return;
      }

      if (this.expanded) {
        this.closeSearch();
      }
    },
    expanded(isExpanded) {
      if (isExpanded) {
        // Capture phase so the dismiss fires before bubble-phase SVG click
        // handlers that might re-trigger or interfere with the search state.
        document.addEventListener('mousedown', this.mousedownOutsideHandler, true);
      } else {
        document.removeEventListener('mousedown', this.mousedownOutsideHandler, true);
      }
    },
    'store.activePreview.value'(preview) {
      // A project-scoped search stays open while the reviewer moves between the
      // project's own documents; it only closes when they leave the project.
      if (this.file.ty === 'project-group') {
        const path = preview?.ty === 'file-diff' ? preview.path :
          preview?.ty === 'proj-page-diff' ? preview.file?.headPath : null;
        if (!path || !this.projectDocumentPaths.has(path)) {
          this.closeSearch();
        }
        return;
      }
      if (preview?.ty === 'proj-page-diff') {
        if (preview.file?.headPath !== this.file.headPath) {
          this.closeSearch();
        }
        return;
      }
      if (preview?.ty === 'file-diff') {
        if (preview.path !== this.file.headPath) {
          this.closeSearch();
        }
        return;
      }
      this.closeSearch();
    },
    async selectedInstances(newInstances: (ComponentInstance | NetInstance)[], oldInstances: (ComponentInstance | NetInstance)[]) {
      // When the visible-page filter changes, selectedIndex may point at a
      // different instance. Re-derive it from the previously-selected
      // instance's identity (pageId + dataSelector).
      const idx = this.selectedIndex;
      if (!this.selectedReference || newInstances.length === 0) return;
      const prev = oldInstances?.[idx];
      if (!prev) {
        this.setSelected(this.selectedReference, this.selectedCategory, Math.min(idx, newInstances.length - 1));
        return;
      }
      const prevKey = 'dataSelector' in prev ? prev.dataSelector : prev.componentId;
      const newIdx = newInstances.findIndex((inst) =>
        inst.pageId === prev.pageId &&
        ('dataSelector' in inst ? inst.dataSelector : inst.componentId) === prevKey,
      );
      if (newIdx !== -1 && newIdx !== idx) {
        this.setSelected(this.selectedReference, this.selectedCategory, newIdx);
      } else if (newIdx === -1) {
        await this.selectInstance(
          this.selectedReference,
          this.selectedCategory,
          Math.min(idx, newInstances.length - 1),
        );
      }
    },
  },

  mounted() {
    this.escapeHandler = this.handleEscape.bind(this);
    this.mousedownOutsideHandler = this.handleMousedownOutside.bind(this);
    document.body.addEventListener('keydown', this.escapeHandler);
    if (this.isSearchOwner) {
      this.resumeFromPersistedStateSafely();
    }
  },

  unmounted() {
    this.navigationAbortController?.abort();
    document.body.removeEventListener('keydown', this.escapeHandler);
    document.removeEventListener('mousedown', this.mousedownOutsideHandler, true);
    this.clearHighlight();
  },

  methods: {
    getCurrentPreviewRoot() {
      return this.$el?.closest?.('.diff-file-box') ?? null;
    },

    async waitForPreviewRoot(pageNameHash: string, timeoutMs = 5000) {
      const rootId = `diff-${pageNameHash}`;
      const start = Date.now();

      while (Date.now() - start < timeoutMs) {
        const root = document.querySelector(`#${CSS.escape(rootId)}`);
        if (root) {
          return root;
        }

        await new Promise((resolve) => setTimeout(resolve, 50));
      }

      return null;
    },

    focusSearchPrompt() {
      this.$nextTick(() => {
        onNextFrame(() => {
          (this.$refs.searchPrompt as HTMLInputElement)?.focus();
        });
      });
    },

    async resumeFromPersistedStateSafely() {
      try {
        await this.resumeFromPersistedState();
      } catch (error) {
        console.error('Failed to resume multi-page search state', error);
      }
    },

    closeSearch(resetState = false) {
      this.expanded = false;
      this.clearHighlight();
      if (resetState) {
        this.multiPageSearchStore.resetSearchState(this.file.headPath);
        return;
      }

      if (this.isSearchOwner) {
        this.multiPageSearchStore.setOpenPage(this.file.headPath, null);
      }
    },

    async resumeFromPersistedState() {
      this.expanded = true;
      if (this.indexByReference.size === 0 && !this.errorMessage) {
        await this.loadIndex();
      }
      // Re-apply highlight if there's a persisted selection targeting this page.
      if (this.selectedReference && this.selectedCategory) {
        // During cross-document navigation this page mounts as it becomes the
        // search owner, so the selected instance may not be resolvable for a
        // tick after the index loads. Wait for it before highlighting, otherwise
        // the highlight/zoom is silently skipped on the first step onto a page.
        const inst = await this.waitForSelectedInstance();
        if (inst) {
          await this.navigateToInstance(inst, this.selectedCategory);
        }
      }
    },

    async waitForSelectedInstance(timeoutMs = 3000) {
      const match = () => {
        const inst = this.selectedInstances[this.selectedIndex];
        return inst && this.pageKeyForInstance(inst) === this.currentPageId ? inst : null;
      };
      const start = Date.now();
      let inst = match();
      while (!inst && Date.now() - start < timeoutMs) {
        await new Promise((resolve) => setTimeout(resolve, 50));
        inst = match();
      }
      return inst;
    },

    clearSelection() {
      this.clearPinnedSelection();
      this.focusSearchPrompt();
    },

    clearPinnedSelection() {
      this.clearHighlight();
      this.setSelected(null, null, 0);
      this.multiPageSearchStore.setOpenPage(this.file.headPath, this.currentPageId);
    },

    handleEscape(event: KeyboardEvent) {
      if (event.key === 'Escape' && this.expanded) {
        event.preventDefault();
        this.closeSearch(true);
      }
    },

    handleMousedownOutside(event: MouseEvent) {
      if (!this.expanded) return;
      // Let the unchanged-sheet filter update the open search before dismissing
      // it so a selection on a newly hidden sheet can move to a visible match.
      if (event.target instanceof Element && event.target.closest('.unchanged-sheets-toggle')) return;
      if (this.$el && !this.$el.contains(event.target)) {
        this.closeSearch(true);
      }
    },

    setSelected(reference: string | null, category: 'Reference' | 'Net' | null, index: number) {
      this.multiPageSearchStore.setSelection(this.file.headPath, reference, category, index);
    },

    clearHighlight() {
      if (this.currentHighlightSvg && this.currentHighlightSelector) {
        toggleHighlightElement(this.currentHighlightSvg, this.currentHighlightSelector, false, this.currentHighlightElementType, false);
        this.currentHighlightSvg = null;
        this.currentHighlightSelector = null;
        this.currentHighlightElementType = 'component';
      }
    },

    async toggleSearchBar(event: MouseEvent) {
      event.preventDefault();
      if (this.expanded) {
        this.closeSearch(true);
        return;
      }

      this.expanded = true;
      this.multiPageSearchStore.setOpenPage(this.file.headPath, this.currentPageId);
      if (this.indexByReference.size === 0 && !this.errorMessage) {
        await this.loadIndex();
      }
      this.focusSearchPrompt();
    },

    async loadIndex() {
      await this.multiPageSearchStore.ensureComponentIndex(this.file, {
        hashToPathMap: this.store.hashToPathMap,
        container: this.store.container,
        diffInfo: this.store.diffInfo,
      });
    },

    // A project group renders each document as its own diff box, so instances
    // are addressed by their document's diff hash; every other view addresses
    // them by convert page id.
    pageKeyForInstance(inst: ComponentInstance | NetInstance): string {
      return this.file.ty === 'project-group' ? inst.pageNameHash : inst.pageId;
    },

    setOpenPageForInstance(targetInstance: ComponentInstance | NetInstance | undefined) {
      const key = targetInstance ? this.pageKeyForInstance(targetInstance) : '';
      this.multiPageSearchStore.setOpenPage(this.file.headPath, key || this.currentPageId);
    },

    async selectInstance(key: string, category: 'Reference' | 'Net', index: number) {
      if (!key) {
        return;
      }

      const instances = category === 'Net' ?
        (this.filteredByNet.get(key) ?? []) :
        (this.filteredByReference.get(key) ?? []);
      const targetInstance = instances[index];
      if (!targetInstance) {
        return;
      }

      this.setSelected(key, category, index);
      this.setOpenPageForInstance(targetInstance);
      await this.navigateToInstance(targetInstance, category);
    },

    async onResultClick(result: MultiPageSearchResult) {
      if (result.category === 'Net') {
        const instances = this.filteredByNet.get(result.key) ?? [];
        let idx = instances.findIndex(
          (inst) => inst.pageId === result.pageId && inst.dataSelector === result.dataSelector,
        );
        if (idx < 0) idx = 0;
        await this.selectInstance(result.key, 'Net', idx);
        return;
      }

      const instances = this.filteredByReference.get(result.key) ?? [];
      let idx = instances.findIndex(
        (inst) => inst.pageId === result.pageId && inst.componentId === result.componentId,
      );
      if (idx < 0) idx = 0;
      await this.selectInstance(result.key, 'Reference', idx);
    },

    activatePreview(pageNameHash: string) {
      const targetHash = `#diff-${pageNameHash}`;
      this.store.suppressNextAnchorScroll.value = true;
      if (window.history.replaceState) {
        window.history.replaceState(window.history.state, '', targetHash);
        this.store.activePath.value = targetHash;
        return;
      }

      window.location.hash = targetHash;
    },

    async resolvePageContainer(inst: ComponentInstance | NetInstance) {
      // A project group renders each document as its own top-level diff box with
      // the schematic inline (no page accordion), so the owning document's box —
      // not an accordion section keyed by convert page id — holds the SVG.
      if (this.file.ty === 'project-group') {
        if (!inst.pageNameHash) {
          return null;
        }
        const currentRoot = this.getCurrentPreviewRoot();
        if (inst.pageNameHash === this.currentPageId && currentRoot instanceof HTMLElement) {
          return currentRoot;
        }
        this.activatePreview(inst.pageNameHash);
        const projectRoot = await this.waitForPreviewRoot(inst.pageNameHash);
        return projectRoot instanceof HTMLElement ? projectRoot : null;
      }

      const currentRoot = this.getCurrentPreviewRoot();

      // When mounted on the parent multi-page-diff, always navigate to the
      // child page's dedicated preview instead of using the accordion.
      if (this.file.ty !== 'multi-page-diff') {
        if (currentRoot?.querySelector(`[data-accordion-head="${CSS.escape(inst.pageId)}"]`)) {
          await openAndScrollToPage(currentRoot, inst.pageId);
          const loadedSvg = await waitForPageSvg(currentRoot, inst.pageId);
          if (!loadedSvg) {
            return null;
          }

          return findPageContainer(currentRoot, inst.pageId);
        }

        const existingContainer = findPageContainer(currentRoot, inst.pageId);
        if (existingContainer) {
          return existingContainer;
        }
      }

      const preview = this.store.activePreview.value;
      if (preview?.ty !== 'proj-page-diff' && preview?.ty !== 'file-diff') {
        return null;
      }

      // A sheet whose document is not part of this review has no row to open.
      // Navigating anyway sets an empty fragment, and the preview the reader was
      // already looking at is replaced by an error naming no file.
      if (!inst.pageNameHash) {
        return null;
      }

      this.activatePreview(inst.pageNameHash);
      const targetRoot = await this.waitForPreviewRoot(inst.pageNameHash);
      if (!targetRoot) {
        return null;
      }

      const loadedSvg = await waitForPageSvg(targetRoot, inst.pageId);
      if (!loadedSvg) {
        return null;
      }

      return findPageContainer(targetRoot, inst.pageId);
    },

    async navigateToInstance(inst: ComponentInstance | NetInstance, category: 'Reference' | 'Net' = 'Reference') {
      if (!inst) return;
      this.navigationAbortController?.abort();
      const abortController = new AbortController();
      this.navigationAbortController = abortController;
      this.clearHighlight();

      try {
        const pageContainer = await this.resolvePageContainer(inst);
        if (!pageContainer || abortController.signal.aborted) {
          console.debug(`No page container found for page ${inst.pageId}`);
          return;
        }

        const liveState = this.multiPageSearchStore.getSearchState(this.file.headPath);
        if (liveState.openPageId !== this.currentPageId) {
          return;
        }
        await this.applyHighlightAndZoom(inst, pageContainer, category, abortController.signal);
      } catch (err) {
        // If the page changed mid-navigation, the current instance is stale
        // and the new page's component will handle highlighting from the store.
        const liveState = this.multiPageSearchStore.getSearchState(this.file.headPath);
        if (liveState.openPageId !== this.currentPageId) return;
        // Still on the same page, unexpected error, surface it.
        throw err;
      } finally {
        if (this.navigationAbortController === abortController) {
          this.navigationAbortController = null;
        }
      }
    },

    async waitForZoomTarget(pageContainer: HTMLElement, signal: AbortSignal, timeoutMs = 5000) {
      const findTarget = () => {
        const svgWrapperEl = pageContainer.querySelector('.svg-wrapper');
        const wrapper = svgWrapperEl ? vueInstance(svgWrapperEl) : null;
        if (svgWrapperEl && wrapper) {
          return {svgWrapperEl, wrapper};
        }
        return null;
      };

      const target = findTarget();
      if (target || signal.aborted) {
        return target;
      }

      return new Promise<{svgWrapperEl: Element; wrapper: Record<string, any>} | null>((resolve) => {
        const listeners = new AbortController();
        let timeout: ReturnType<typeof setTimeout>;
        const finish = (result: {svgWrapperEl: Element; wrapper: Record<string, any>} | null) => {
          clearTimeout(timeout);
          listeners.abort();
          resolve(result);
        };
        timeout = setTimeout(() => finish(null), timeoutMs);
        const onInspectorDataLoad = () => {
          const readyTarget = findTarget();
          if (!readyTarget) return;
          finish(readyTarget);
        };
        const onAbort = () => finish(null);

        pageContainer.addEventListener('inspectordataload', onInspectorDataLoad, {signal: listeners.signal});
        signal.addEventListener('abort', onAbort, {once: true, signal: listeners.signal});
      });
    },

    async zoomToInstance(wrapper: Record<string, any>, schematicSvg: SVGSVGElement, selector: string, pageId: string, signal: AbortSignal, timeoutMs = 5000) {
      const start = Date.now();

      while (!signal.aborted && Date.now() - start < timeoutMs) {
        try {
          wrapper.$refs?.ecadInspectorFrame?.showInspector?.(false);
          await wrapper.dollyZoom?.(schematicSvg, selector);
          return;
        } catch (error) {
          await new Promise((resolve) => setTimeout(resolve, 50));

          const refreshedWrapper = vueInstance(schematicSvg.closest('.svg-wrapper')) ?? wrapper;
          if (refreshedWrapper) {
            wrapper = refreshedWrapper;
          }

          if (Date.now() - start >= timeoutMs) {
            console.debug(`Failed to zoom schematic for page ${pageId}`, error);
          }
        }
      }
    },

    async waitForSchematicSvg(pageContainer: HTMLElement, svgWrapperEl: Element, targetSelector: string, signal: AbortSignal, timeoutMs = 30000) {
      // The grabber/toolbar icons live in `.inspector-header`; the diagram lives
      // in `.svg-schematic`, so scoping there avoids grabbing a toolbar icon
      // regardless of the schematic SVG's class. Poll for the schematic AND the
      // element we're about to highlight: a freshly navigated document injects
      // the schematic and then renders its contents, so highlighting before the
      // target exists clones nothing and is wiped when the schematic finishes
      // rendering. Re-querying each tick also returns the final SVG rather than a
      // mid-render one that gets replaced.
      const stableForMs = 500;
      const start = Date.now();
      let candidate: SVGSVGElement | null = null;
      let stableSince = 0;
      while (!signal.aborted && Date.now() - start < timeoutMs) {
        const currentSvgWrapperEl = pageContainer.querySelector('.svg-wrapper') ?? svgWrapperEl;
        const svg = currentSvgWrapperEl.querySelector<SVGSVGElement>('.svg-schematic svg');
        if (svg && (!targetSelector || svg.querySelector(targetSelector))) {
          if (candidate !== svg) {
            candidate = svg;
            stableSince = Date.now();
          } else if (Date.now() - stableSince >= stableForMs) {
            return svg;
          }
        } else {
          candidate = null;
          stableSince = 0;
        }
        await new Promise((resolve) => setTimeout(resolve, 50));
      }
      return null;
    },

    async applyHighlightAndZoom(inst: ComponentInstance | NetInstance, pageContainer: HTMLElement, category: 'Reference' | 'Net', signal: AbortSignal) {
      const zoomTarget = await this.waitForZoomTarget(pageContainer, signal);
      if (!zoomTarget || signal.aborted) {
        console.debug(`No .svg-wrapper found in page ${inst.pageId}`);
        return;
      }
      const {svgWrapperEl, wrapper} = zoomTarget;

      const isNet = category === 'Net';
      const elementType: HighlightElementType = isNet ? 'net' : 'component';

      let highlightSelector: string;
      let zoomSelector: string;
      if (isNet) {
        const netInst = inst as NetInstance;
        // Highlight ALL clusters of this net on this page
        const allNetInstances = this.selectedInstances.filter(
          (i) => i.pageId === inst.pageId,
        ) as NetInstance[];
        highlightSelector = allNetInstances.map((i) => i.dataSelector).join(', ');
        zoomSelector = netInst.dataSelector;
      } else {
        const compInst = inst as ComponentInstance;
        highlightSelector = `g[data-id='${CSS.escape(compInst.componentId)}']`;
        zoomSelector = highlightSelector;
      }

      const schematicSvg = await this.waitForSchematicSvg(pageContainer, svgWrapperEl, zoomSelector, signal);
      if (!schematicSvg || signal.aborted) {
        console.debug(`No schematic SVG found in page ${inst.pageId}`);
        return;
      }
      const currentSvgWrapperEl = schematicSvg.closest('.svg-wrapper') ?? svgWrapperEl;
      const currentWrapper = vueInstance(currentSvgWrapperEl) ?? wrapper;

      toggleHighlightElement(schematicSvg, highlightSelector, true, elementType, false);
      this.currentHighlightSvg = schematicSvg;
      this.currentHighlightSelector = highlightSelector;
      this.currentHighlightElementType = elementType;

      if (currentWrapper?.dollyZoom) {
        await this.zoomToInstance(currentWrapper, schematicSvg, zoomSelector, inst.pageId, signal);
      }

      // Center the SVG in the viewport after zoom so it's not off-screen
      // when cycling across pages.
      if (!signal.aborted) {
        currentSvgWrapperEl.scrollIntoView({behavior: 'instant', block: 'center'});
      }
    },

    async nextInstance() {
      if (this.totalInstances === 0) return;
      const newIdx = (this.selectedIndex + 1) % this.totalInstances;
      await this.selectInstance(this.selectedReference, this.selectedCategory ?? 'Reference', newIdx);
    },

    async prevInstance() {
      if (this.totalInstances === 0) return;
      const newIdx = (this.selectedIndex - 1 + this.totalInstances) % this.totalInstances;
      await this.selectInstance(this.selectedReference, this.selectedCategory ?? 'Reference', newIdx);
    },

    async focusSelectedInstance() {
      if (this.totalInstances === 0) {
        return;
      }

      await this.selectInstance(this.selectedReference, this.selectedCategory ?? 'Reference', this.selectedIndex);
    },
  },
});
</script>

<template>
  <div class="search">
    <a
      style="text-decoration: none;"
      class="search-toggle-button"
      @click="toggleSearchBar"
    >
      <span
        class="btn-octicon tooltip"
        :data-tooltip-content="searchTooltip"
        data-position="bottom-center"
        data-variation="inverted tiny"
      >
        <i
          class="icon tw:flex tw:items-center tw:justify-center"
          style="padding-left: 8px"
        ><svg-icon
          name="octicon-search"
          :size="16"
        /></i>
      </span>
    </a>
    <div
      v-show="expanded"
      class="search-bar"
    >
      <div
        v-if="isLoading"
        class="loading-state"
      >
        <div class="ui active inline tiny loader" />
        <span class="tw:ml-2 tw:text-xs">Loading…</span>
      </div>
      <div
        v-else-if="errorMessage"
        class="ui negative tiny message"
      >
        {{ errorMessage }}
      </div>
      <template v-else>
        <div
          v-if="selectedReference && totalInstances > 0"
          class="iterate-controls"
        >
          <button
            v-if="totalInstances > 1"
            type="button"
            class="ui mini basic icon button"
            :disabled="totalInstances <= 1"
            @click="prevInstance"
          >
            <svg-icon
              name="octicon-chevron-left"
              :size="14"
            />
          </button>
          <button
            type="button"
            class="iterate-label iterate-label-button"
            data-test="selected-instance"
            aria-label="Show selected component in schematic"
            @click="focusSelectedInstance"
          >
            <div class="tw:font-mono">
              {{ selectedReference }}
            </div>
            <div class="tw:text-text-light-1 tw:text-xs">
              <template v-if="totalInstances > 1">
                {{ selectedIndex + 1 }} of {{ totalInstances }} — {{ selectedPageName }}
              </template>
              <template v-else>
                {{ selectedPageName }}
              </template>
            </div>
          </button>
          <button
            v-if="totalInstances > 1"
            type="button"
            class="ui mini basic icon button"
            :disabled="totalInstances <= 1"
            @click="nextInstance"
          >
            <svg-icon
              name="octicon-chevron-right"
              :size="14"
            />
          </button>
        </div>
        <div class="search-input-wrapper">
          <input
            ref="searchPrompt"
            v-model="searchQuery"
            tabindex="-1"
            class="search-prompt"
            type="text"
            :placeholder="searchPlaceholder"
          >
        </div>
        <div
          v-if="searchQuery && visibleResults.length > 0"
          class="search-results"
        >
          <template
            v-for="group in visibleResultsByCategory"
            :key="group.category"
          >
            <div class="search-category-name">
              {{ group.category }}
            </div>
            <div class="search-results-list">
              <div
                v-for="result in group.results"
                :key="`${result.category}::${result.key}`"
                class="search-result"
                @click="onResultClick(result)"
              >
                <div class="search-result-title">
                  {{ result.key }}
                </div>
                <div
                  v-if="result.description"
                  class="search-result-description"
                >
                  {{ result.description }}
                </div>
              </div>
            </div>
          </template>
        </div>
        <div
          v-else-if="searchQuery"
          class="search-no-results"
        >
          No results found.
        </div>
      </template>
    </div>
  </div>
</template>

<style scoped>
.search-bar {
  top: 60px;
  right: 5px;
  position: absolute;
  z-index: 999;
  width: min(28em, calc(100vw - 24px));
}
.search-input-wrapper {
  background: var(--color-body);
  border: 1px solid var(--color-secondary);
  border-radius: var(--border-radius);
  padding: 4px;
  width: 100%;
  box-sizing: border-box;
}
.search-prompt {
  width: 100%;
  padding: 6px 8px;
  border: 1px solid var(--color-secondary);
  border-radius: var(--border-radius);
  background: var(--color-body);
  color: var(--color-text);
  font-size: 14px;
  outline: none;
  box-sizing: border-box;
}
.search-prompt:focus {
  border-color: var(--color-primary);
}
.search-results {
  width: 100%;
  max-height: 60vh;
  overflow: hidden;
  background: var(--color-body);
  border: 1px solid var(--color-secondary);
  border-radius: var(--border-radius);
  margin-top: 2px;
  display: grid;
  grid-template-columns: 6.5rem minmax(0, 1fr);
}
.search-category-name {
  padding: 10px 12px;
  font-weight: var(--font-weight-bold);
  color: var(--color-text-light);
  background: var(--color-body);
  border-right: 1px solid var(--color-secondary);
  font-size: 12px;
  display: flex;
  align-items: flex-start;
}
.search-category-name ~ .search-category-name {
  border-top: 1px solid var(--color-secondary);
}
.search-category-name ~ .search-category-name + .search-results-list {
  border-top: 1px solid var(--color-secondary);
}
.search-results-list {
  min-width: 0;
  max-height: 60vh;
  overflow-y: auto;
}
.search-result {
  padding: 8px 12px;
  cursor: pointer;
  border-bottom: 1px solid var(--color-secondary);
}
.search-result:last-child {
  border-bottom: none;
}
.search-result:hover {
  background: var(--color-secondary);
}
.search-result-title {
  font-weight: var(--font-weight-semibold);
  color: var(--color-text);
}
.search-result-description {
  font-size: 12px;
  color: var(--color-text-light-1);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.search-no-results {
  width: 100%;
  padding: 12px;
  text-align: center;
  color: var(--color-text-light-1);
  background: var(--color-body);
  border: 1px solid var(--color-secondary);
  border-radius: var(--border-radius);
  margin-top: 2px;
  box-sizing: border-box;
}
.loading-state {
  background: var(--color-body);
  border: 1px solid var(--color-secondary);
  border-radius: var(--border-radius);
  padding: 8px 12px;
  display: flex;
  align-items: center;
}
.iterate-controls {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 8px;
  margin-bottom: 4px;
  background: var(--color-body);
  border: 1px solid var(--color-secondary);
  border-radius: var(--border-radius);
}
.iterate-label {
  flex: 1;
  text-align: center;
}

.iterate-label-button {
  min-width: 0;
  padding: 4px 6px;
  border: 0;
  border-radius: var(--border-radius);
  background: transparent;
  color: inherit;
  cursor: pointer;
}

.iterate-label-button:hover {
  background: var(--color-hover);
}
</style>
